import React, { useState } from "react";
import { toast } from "react-toastify";
import { useParams, useSearchParams } from "react-router-dom";
import { Button, Typography, Box } from "@mui/material";
import { Add, Edit } from "@mui/icons-material";

import FormDrawer from "components/Shared/FormDrawer";
import AppTable from "components/Shared/AppTable";
import { BaseTabs } from "components/Shared/BaseTabs";
import {
  useAddDriverRouteAssignmentMutation,
  useUpdateDriverRouteAssignmentMutation,
  useLazyListDriverRouteAssignmentQuery,
} from "store/services/driverRouteAssignmentApi";
import { useAuth } from "hooks";
import { useTablePagination } from "hooks";
import { useDriverDetailContext } from "contexts/DriverDetailContext";
import DriverRouteAssignmentForm from "./DriverAssignedRouteForm";

const DriverAssignedRoutesTabs = () => {
  const { id } = useParams();
  const { isDriver } = useAuth();
  const { driverDetail } = useDriverDetailContext();
  const [showForm, setShowForm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const driverId = isDriver ? driverDetail?._id : id;

  const assignmentTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const [fetchAssignments, { data, isLoading, isFetching, error }] =
    useLazyListDriverRouteAssignmentQuery();

  const [addAssignment] = useAddDriverRouteAssignmentMutation();
  const [updateAssignment] = useUpdateDriverRouteAssignmentMutation();

  React.useEffect(() => {
    if (driverId) {
      fetchAssignments({
        driver: driverId,
        page: currentPage,
        limit: perPage,
        active: activeTab === "Active",
        ...(keyword && { keyword }),
      });
    }
  }, [activeTab, currentPage, fetchAssignments, keyword, perPage, driverId]);

  const handleSubmit = async (formData) => {
    try {
      if (selectedAssignment) {
        await updateAssignment({
          _id: selectedAssignment._id,
          driver: driverId,
          ...formData,
        }).unwrap();
        toast.success("Driver route assignment updated successfully");
      } else {
        await addAssignment({ driver: driverId, ...formData }).unwrap();
        toast.success("Driver route assignment added successfully");
      }
      setShowForm(false);
      setSelectedAssignment(null);
    } catch (err) {
      toast.error("An error occurred");
    }
  };

  const columns = [
    // {
    //   name: "Assignment ID",
    //   selector: (row) => row.assignmentId || "N/A",
    //   sortable: true,
    // },
    {
      name: "Route Name",
      selector: (row) => row.route?.routeName || "N/A",
      cell: (row) => row.route?.routeName || "N/A",
      sortable: true,
    },
    {
      name: "Primary",
      selector: (row) => row.isPrimary,
      cell: (row) => (row.isPrimary ? "Yes" : "No"),
      sortable: true,
    },
    // {
    //   name: "Status",
    //   selector: (row) => row.status || "N/A",
    //   cell: (row) => row.status || "N/A",
    //   sortable: true,
    // },
    {
      name: "Assignment Date",
      selector: (row) => row.assignmentDate,
      cell: (row) => (
        <span>
          {row.assignmentDate
            ? new Date(row.assignmentDate).toLocaleDateString()
            : "N/A"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button
          onClick={() => {
            setSelectedAssignment(row);
            setShowForm(true);
          }}
        >
          <Edit fontSize="small" />
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginRight: "10px",
        }}
      >
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedAssignment(null);
            setShowForm(true);
          }}
        >
          Add Route Assignment
        </Button>
      </Box>
      <BaseTabs tabs={assignmentTabs} defaultValue={activeTab} />
      <AppTable
        columns={columns}
        data={data?.data || []}
        progressPending={isLoading || isFetching}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={data?.totalItems || 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        noDataComponent={
          <Typography py={3} textAlign="center" color="textSecondary">
            No route assignments found
          </Typography>
        }
      />
      <FormDrawer
        open={showForm}
        onClose={() => setShowForm(false)}
        title={`${selectedAssignment ? "Edit" : "Add"} Route Assignment`}
      >
        <DriverRouteAssignmentForm
          assignment={selectedAssignment}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          isSubmitting={false}
          driverId={driverId}
        />
      </FormDrawer>
    </Box>
  );
};

export default DriverAssignedRoutesTabs;
